/* contentDefaults.js — snapshot of what's currently hardcoded on the
 * public site, used to pre-fill the admin form for sections that have
 * no CMS data yet.
 *
 * Goal: when an admin opens /admin/content/home → Logo strip for the
 * first time, they see the 8 logos that are already live, not an empty
 * form. They can rename, remove, reorder, add new, and Save Draft or
 * Publish. If they delete everything and publish, the CMS becomes the
 * source of truth (empty strip) — so the pre-fill is only cosmetic.
 */

// Mirror of `clientLogosV2` in /app/frontend/src/App.js — 5 client brands
const _logos = {
  veronique: {
    brand: "Veronique Gabai",
    media:
      "https://customer-assets.emergentagent.com/job_shopify-ux-revamp/artifacts/74tni3x0_2825e286-618a-789d-c6bf-1187d8bec1d4-w.png",
    href: "",
  },
  teddy: {
    brand: "Teddy Blake NYC",
    media:
      "https://customer-assets.emergentagent.com/job_shopify-ux-revamp/artifacts/ybb2cwe5_logo-black-popup-w.png",
    href: "",
  },
  madam: {
    brand: "Madam Glam",
    media:
      "https://customer-assets.emergentagent.com/job_shopify-ux-revamp/artifacts/rbg2w0na_MadamGlam_Logo_Desktop_Pro_59bf4872-8eb7-4482-ab03-ee0533ba98b8-w.png",
    href: "",
  },
  mg: {
    brand: "MG",
    media:
      "https://customer-assets.emergentagent.com/job_shopify-ux-revamp/artifacts/24xxyvv0_mg_logo_1X_a48efdf6-973b-4e68-b2e5-30f56d5c5124-w.png",
    href: "",
  },
  myw: {
    brand: "MYW",
    media:
      "https://customer-assets.emergentagent.com/job_shopify-ux-revamp/artifacts/yf6y72pp_myw_logo_type-w.png",
    href: "",
  },
};

// Pre-fill shape: 8 slots split into 2 rows of 4 (matches the current
// live marquee exactly). Speeds default to 45s per loop (matches
// `animation-duration` in index.css for .animate-marquee-ltr/rtl).
const HOME_LOGO_STRIP_DEFAULTS = {
  style: "color",
  line_1: [
    _logos.veronique,
    _logos.teddy,
    _logos.madam,
    _logos.mg,
  ],
  line_1_speed: 45,
  line_2: [
    _logos.myw,
    _logos.veronique,
    _logos.madam,
    _logos.myw,
  ],
  line_2_speed: 45,
};

// Mirror of the Hero copy in `HeroSection` (App.js lines ~1003–1016).
// First-open CMS state shows the real live copy in every input.
const HOME_HERO_DEFAULTS = {
  eyebrow: "Shopify Experts · Since 2014",
  title_line_1: "One Partner.",
  title_emphasis: "Every",
  title_line_2: "Shopify Need.",
  subtitle:
    "Skip the hassle of searching for freelancers. Our Shopify experts handle everything — from quick fixes to full-scale projects — with speed, quality, and personal attention.",
  cta_primary_label: "Start a Project",
  cta_primary_href: "#contact",
  cta_secondary_label: "See How It Works",
  cta_secondary_href: "#process",
};

// Mirror of the scroll-reveal paragraph in `DevelopmentOverview`
// (App.js ~line 2170). Single plain-text body; the animation and
// display type are design-locked.
const HOME_DEVELOPMENT_OVERVIEW_DEFAULTS = {
  body:
    "From small fixes to full Shopify builds — done right. One team for every Shopify and Shopify Plus need — custom themes, advanced functionality, integrations, and optimized workflows. Over 250 custom features built. If it's logical, it's codable.",
};

// Mirror of `HEROVIDEO_SRC` in `HeroVideoV2` (App.js ~line 2295).
const HOME_HERO_VIDEO_DEFAULTS = {
  media:
    "https://customer-assets.emergentagent.com/job_shopify-ux-revamp/artifacts/wxuo5if7_invideo-ai-1080%20Stop%20Searching.%20Start%20Shipping%E2%80%94Your%20Shop%202026-02-06.mp4",
};

// Mirror of `MediaBackgroundV5` (App.js ~line 5141). Background is a
// blurred Pexels video with eyebrow + headline + body overlaid.
const HOME_MEDIA_BACKGROUND_DEFAULTS = {
  eyebrow: "Craft & scale",
  headline_main: "Built for edge cases.",
  headline_italic: "Engineered",
  headline_tail: "to scale.",
  body:
    "A few of the customizations we've shipped for merchants who refused to settle for off-the-shelf apps.",
  media: "https://videos.pexels.com/video-files/3209828/3209828-hd_1920_1080_25fps.mp4",
};

// Mirror of `marketing` (App.js ~line 3031) + `MarketingPinnedV5`
// header copy. Each panel becomes one full-screen title in the
// pinned horizontal scroll. The 6 SVG decorative shapes are
// design-locked and indexed by position (0–5).
const HOME_MARKETING_PINNED_DEFAULTS = {
  eyebrow: "Marketing 360°",
  headline_main: "A single partner for every",
  headline_accent: "digital task",
  cta_primary_label: "Talk to us",
  cta_primary_href: "#contact",
  cta_secondary_label: "See all Marketing Services",
  cta_secondary_href: "#services",
  panels: [
    { title: "Paid Marketing",      body: "Targeted campaigns across channels — optimized for ROAS and scaled revenue." },
    { title: "Email Marketing",     body: "Automated, personalized flows and campaigns that convert and retain." },
    { title: "Social Management",   body: "Presence as an asset — content that engages, builds loyalty, and sells." },
    { title: "SEO",                 body: "Audits and competitive strategy that put your brand in front of the right audience." },
    { title: "Influencer Marketing", body: "Personalized influencer campaigns that raise awareness and lift sales." },
    { title: "Content Creation",    body: "Strong content makes your store more appealing, easier to navigate, and higher-converting." },
  ],
};
const HOME_SERVICES_GRID_DEFAULTS = {
  eyebrow: "Our Shopify Services",
  headline_main: "Development expertise — across every surface of",
  headline_accent: "your store",
  description:
    "Comprehensive Shopify services tailored to your business needs — from the storefront to the checkout to the back office.",
  cta_label: "See all services",
  cta_href: "#contact",
  hover_hint: "Hover a service to expand",
  items: [
    {
      tag_number: "01",
      tag_label: "Theme",
      title: "Custom Theme Development",
      body:
        "Responsive Shopify themes that combine smart design with advanced functionality to boost engagement and conversions.",
      highlights: ["Bespoke theme builds", "Responsive & accessible", "Conversion-tuned UI"],
      icon: "Palette",
      media:
        "https://images.unsplash.com/photo-1559028012-481c04fa702d?auto=format&fit=crop&w=1200&q=80",
    },
    {
      tag_number: "02",
      tag_label: "Ops",
      title: "Store Maintenance",
      body:
        "Regular updates and optimizations that keep your store running smoothly and scaling with your business.",
      highlights: ["Updates & patches", "Performance tuning", "Ongoing monitoring"],
      icon: "Wrench",
      media:
        "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1200&q=80",
    },
    {
      tag_number: "03",
      tag_label: "Build",
      title: "App Development",
      body:
        "Web applications that streamline operations, engage customers, and deliver a smooth experience end-to-end.",
      highlights: ["Custom storefronts", "Admin apps & tooling", "API integrations"],
      icon: "Layers",
      media:
        "https://videos.pexels.com/video-files/3141207/3141207-hd_1920_1080_25fps.mp4",
    },
    {
      tag_number: "04",
      tag_label: "Custom",
      title: "Shopify Custom Tasks",
      body:
        "Push beyond Shopify limits. We customize your store to meet unique requirements — the way you actually work.",
      highlights: ["Workflow automations", "Custom features", "Unique requirements"],
      icon: "Code2",
      media:
        "https://videos.pexels.com/video-files/3209828/3209828-hd_1920_1080_25fps.mp4",
    },
    {
      tag_number: "05",
      tag_label: "Migrate",
      title: "Migration to Shopify",
      body:
        "Hassle-free migrations that unlock the full potential of Shopify and set your business up for long-term growth.",
      highlights: ["Zero-downtime moves", "Data integrity", "Re-platform strategy"],
      icon: "Shuffle",
      media:
        "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&w=1200&q=80",
    },
    {
      tag_number: "06",
      tag_label: "Partner",
      title: "White Label Development",
      body:
        "Our in-house team delivers reliable, scalable white-label Shopify development with full support for your clients.",
      highlights: ["Partner delivery", "Full lifecycle support", "Your brand, our team"],
      icon: "Briefcase",
      media:
        "https://videos.pexels.com/video-files/2887463/2887463-hd_1920_1080_25fps.mp4",
    },
  ],
};

// Mirror of `COLLAGE_TILES_V5` + `trustMetrics` + the In-Motion block
// in `EditorialCollageV5` (App.js). First-open CMS state reflects the
// exact copy and media currently live so admins can tweak in place.
// Tile 4 used to be a hard-coded yellow color swatch — per design
// decision it's now a normal media slot seeded with a warm editorial
// image that an admin can swap at any time.
const HOME_EDITORIAL_COLLAGE_DEFAULTS = {
  eyebrow: "In Motion",
  headline_main: "Stories shipped, pixels poured,",
  headline_italic: "stores",
  headline_accent: "scaled",
  body:
    "Six years. 250+ Shopify storefronts. Editorial creative, performance marketing, and full-stack development — all under one yellow roof.",
  cta_label: "View the archive",
  cta_href: "#work",
  // trustMetrics
  stat_1_value: "250+",
  stat_1_label: "Custom Features",
  stat_2_value: "10+",
  stat_2_label: "Years on Shopify",
  stat_3_value: "24–48h",
  stat_3_label: "Turnaround",
  stat_4_value: "30+",
  stat_4_label: "Trusted Stores",
  // Five asymmetric tiles (live URLs lifted directly from App.js)
  tile_1_media:
    "https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&w=1600&q=80",
  tile_1_tag: "",
  tile_2_media:
    "https://videos.pexels.com/video-files/3209828/3209828-hd_1920_1080_25fps.mp4",
  tile_2_tag: "STOREFRONT · LIVE",
  tile_3_media:
    "https://images.unsplash.com/photo-1571945153237-4929e783af4a?auto=format&fit=crop&w=1600&q=80",
  tile_3_tag: "",
  tile_4_media:
    "https://images.unsplash.com/photo-1558769132-cb1aea458c5e?auto=format&fit=crop&w=1600&q=80",
  tile_4_tag: "BRAND COLOR · 2024",
  tile_5_media:
    "https://images.unsplash.com/photo-1542291026-7eec264c27ff?auto=format&fit=crop&w=1600&q=80",
  tile_5_tag: "",
};

// Mirror of `creative` array (App.js ~line 3040) used in
// `CreativeCapabilitiesV5`. Six capability cards plus header copy
// matching the Pillar pattern. Each card carries an icon (lucide
// name from SERVICE_ICON_OPTIONS) and a hover-reveal background
// media URL — mirrors `CREATIVE_BG_MEDIA_V5`.
const HOME_CREATIVE_CAPABILITIES_DEFAULTS = {
  eyebrow: "Creative Capabilities",
  headline_main: "Creative thinking.",
  headline_italic: "Proven",
  headline_accent: "strategies",
  description:
    "From Shopify design and advanced UX/UI to video production and social ad creatives — we craft digital experiences that are clean, cohesive, and impactful.",
  cta_label: "Work with us",
  cta_href: "#contact",
  items: [
    {
      title: "Advanced UI/UX",
      body: "Beyond aesthetics — precision-built eCommerce design that drives results.",
      icon: "Palette",
      media:
        "https://images.unsplash.com/photo-1559028012-481c04fa702d?auto=format&fit=crop&w=2000&q=80",
    },
    {
      title: "Shopify Web Design",
      body: "Sharp design that performs — where creativity meets conversion at scale.",
      icon: "Code2",
      media:
        "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?auto=format&fit=crop&w=2000&q=80",
    },
    {
      title: "Video Ads Creative",
      body: "From storyboard to screen — videos designed to convert and scale performance.",
      icon: "Video",
      media:
        "https://videos.pexels.com/video-files/854149/854149-hd_1920_1080_30fps.mp4",
    },
    {
      title: "Social Ads Creative",
      body: "Concept to conversion: audience × message × platform × timing, optimized.",
      icon: "MousePointerClick",
      media:
        "https://images.unsplash.com/photo-1611162617474-5b21e879e113?auto=format&fit=crop&w=2000&q=80",
    },
    {
      title: "Brand Identity",
      body: "Logos, visual systems, and guidelines that make your brand instantly recognizable.",
      icon: "PenTool",
      media:
        "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?auto=format&fit=crop&w=2000&q=80",
    },
    {
      title: "Motion Design",
      body: "Animated product reveals, micro-interactions, and campaign motion that make stores move.",
      icon: "Film",
      media:
        "https://images.unsplash.com/photo-1620712943543-bcc4688e7485?auto=format&fit=crop&w=2000&q=80",
    },
  ],
};

// Mirror of `caseStudiesV2` + `CASE_GALLERY_V5` + `CaseStudiesV5`
// header copy (App.js ~line 6620). Each project carries a primary
// image, secondary detail image, tasks count, partner-since year and
// a list of services rendered as pills.
const HOME_CASE_STUDIES_DEFAULTS = {
  eyebrow: "Featured Clients",
  headline: "Stores we've shaped, scaled, and shipped for.",
  description:
    "A small sample of long-term partnerships — where every pixel, pipeline, and pageview has our fingerprints.",
  cta_label: "View project",
  cta_href: "#contact",
  partner_since_label: "Partner Since",
  completed_label: "Completed",
  items: [
    {
      title: "Veronique Gabai",
      body:
        "Sunshine for the Senses and for the Soul — luxury perfumes, fragrance jewelry & beauty.",
      tasks: "320+",
      since: "2020",
      highlights: [
        "Shopify theme customization",
        "Custom development features",
        "Store management",
        "360° digital services",
      ],
      media:
        "https://static.wixstatic.com/media/1dfc4b_5fdc10e22982473fb394f844437aa4f4~mv2.jpeg/v1/fill/w_900,h_1000,al_c,q_85,enc_avif,quality_auto/1dfc4b_5fdc10e22982473fb394f844437aa4f4~mv2.jpeg",
      secondary_media:
        "https://images.unsplash.com/photo-1611605698335-8b1569810432?auto=format&fit=crop&w=1200&q=80",
    },
    {
      title: "SWAND",
      body: "A modern candle-care tool designed to reconnect with your flame.",
      tasks: "75+",
      since: "2025",
      highlights: [
        "Shopify theme customization",
        "Custom development features",
        "Store management",
        "360° digital services",
      ],
      media:
        "https://static.wixstatic.com/media/1dfc4b_fa8443c372284a1d92b0f2e5060b6959~mv2.webp/v1/fill/w_900,h_1000,al_c,q_85,enc_avif,quality_auto/1dfc4b_fa8443c372284a1d92b0f2e5060b6959~mv2.webp",
      secondary_media:
        "https://images.unsplash.com/photo-1572044162444-ad60f128bdea?auto=format&fit=crop&w=1200&q=80",
    },
    {
      title: "Cafe Joyeux",
      body:
        "Served from the heart — coffee with a mission. A café restaurant like no other.",
      tasks: "125+",
      since: "2023",
      highlights: [
        "Shopify theme customization",
        "Custom development features",
        "Store management",
        "360° digital services",
      ],
      media:
        "https://static.wixstatic.com/media/1dfc4b_eabf130d251f4293a250d08d7a480296~mv2.jpg/v1/fill/w_900,h_1000,al_c,q_85,enc_avif,quality_auto/1dfc4b_eabf130d251f4293a250d08d7a480296~mv2.jpg",
      secondary_media:
        "https://images.unsplash.com/photo-1626785774573-4b799315345d?auto=format&fit=crop&w=1200&q=80",
    },
  ],
};

// Mirror of `testimonials` (App.js ~line 4142) used by `TestimonialsV5`.
// `title` = quote, `body` = name, `role` = "Owner · sclarandis.com".
const HOME_TESTIMONIALS_DEFAULTS = {
  eyebrow: "In Their Own Words",
  items: [
    {
      title:
        "Working with Sunday Digital was a great experience from start to finish. The team was responsive, professional, and really understood my brand vision.",
      body: "Sarah Sclarandis",
      role: "Owner · sclarandis.com",
    },
    {
      title:
        "Consistently delivers exceptional service. Prompt support, reliable code, and real responsiveness. The longevity of their solutions speaks for itself.",
      body: "Teddy W.",
      role: "CFO · Rose Forever NYC",
    },
    {
      title:
        "Our decade-long partnership is a testament to their reliability and expertise. Highly recommendable for top-notch IT development.",
      body: "Sam W.",
      role: "Founder · Teddy Blake NYC",
    },
    {
      title:
        "They built our website and B2B store with real care. Fast, reliable, cost-conscious — and they genuinely care about our mission.",
      body: "Julie A.",
      role: "General Manager · Cafe Joyeux NYC",
    },
  ],
};

// Mirror of `faqs` (App.js ~line 4218) used by `FAQ`.
// `title` = question, `body` = answer.
const HOME_FAQ_DEFAULTS = {
  eyebrow: "Got Questions?",
  headline: "Frequently asked questions.",
  description: "Everything you need to know before starting a project.",
  items: [
    {
      title: "Why choose Sunday Digital?",
      body:
        "Compared to development platforms, freelancers, or typical agencies, we offer professional experts for all your Shopify needs — design, development, and complex projects — delivering speed, precision, and consistency without the hassle of managing multiple providers.",
    },
    {
      title: "Are you an agency?",
      body:
        "We're not a typical agency. We're a dedicated Shopify team that handles everything in-house — from small updates to full-scale projects — providing the speed, expertise, and consistency you won't get from freelancers or larger agencies.",
    },
    {
      title: "Do you test your work and provide revisions?",
      body:
        "Yes. Every project is fully tested, and we offer unlimited revisions. At the start of each project, we provide expert consulting to ensure the final result aligns with your goals.",
    },
    {
      title: "What's the turnaround for small tasks and large projects?",
      body:
        "Small tasks are usually completed within 24–48 hours, with express options for urgent issues. Larger projects follow a clear timeline set at kickoff — ensuring fast, high-quality delivery.",
    },
    {
      title: "Is there a minimum spend or commitment?",
      body:
        "No strict minimum spend or long-term commitment. Pay-per-task options are available, alongside tailored packages designed to fit your budget and project scope. Get in touch for a detailed quote & promotional packages.",
    },
  ],
};

// Mirror of `ClosingV5` (App.js ~line 6949). Tagline marquee preserves
// the alternating solid / outline rhythm — `solid` is "solid" or
// "outline" (any other value falls back to alternating).
const HOME_CLOSING_CTA_DEFAULTS = {
  status_label: "Still taking new projects",
  counter_text: "03 · 26",
  statement_line_1: "Great brands",
  statement_line_2: "aren't built",
  statement_line_3_main: "overnight",
  statement_line_3_period: ".",
  signature_brand: "Sunday Digital",
  signature_est: "Est. 2018",
  signature_tag: "A 360° digital studio",
  tagline_words: [
    { title: "Strategy", solid: "solid" },
    { title: "Design",   solid: "outline" },
    { title: "Code",     solid: "solid" },
    { title: "Growth",   solid: "outline" },
    { title: "Brand",    solid: "solid" },
    { title: "Commerce", solid: "outline" },
  ],
  email_eyebrow: "Tell us about your project",
  email: "hello@sundaydigital.io",
  mobile_cta_label: "Write us",
  location: "Brooklyn, NY",
  hours: "Open Mon–Fri",
  timezone: "EST",
  handle_label: "@sundaydigital",
  handle_href: "https://instagram.com/sundaydigital",
};

// ============================================================
// SHOPIFY DEV defaults — mirror the live copy in
// /app/frontend/src/pages/ShopifyDev.jsx and ShopifyDevV2.jsx so
// /admin/content/shopify-dev opens fully pre-populated.
// ============================================================

const SDEV_HERO_DEFAULTS = {
  breadcrumb: "Shopify Development",
  eyebrow: "eCommerce · Shopify & Shopify Plus",
  title_line_1: "Built on Shopify.",
  title_emphasis: "Engineered",
  title_line_2: "to scale",
  subtitle:
    "One boutique team, every layer of Shopify. From storefront craft to checkout engineering, we partner with brands ready to move beyond templates and app bloat.",
  cta_primary_label: "Start a Project",
  cta_secondary_label: "See capabilities",
  cta_secondary_href: "#capabilities",
};

const SDEV_TRUST_STRIP_DEFAULTS = {
  items: [
    { title: "In-house team" },
    { title: "250+ stores shipped" },
    { title: "Shopify Plus partner" },
    { title: "10+ years" },
    { title: "Brooklyn · Remote" },
  ],
};

const SDEV_WHAT_YOU_GET_DEFAULTS = {
  eyebrow: "What you get",
  headline_main: "Partner with us — and",
  headline_italic: "feel",
  headline_accent: "the difference",
  description:
    "Four commitments that shape every engagement — from a quick fix to a multi-quarter Shopify Plus build.",
  items: [
    { tag_number: "01", title: "Free strategic audit",
      body: "Every engagement starts with a no-cost growth audit — what's working, what's leaking conversion, and what to fix first." },
    { tag_number: "02", title: "Direct senior contact",
      body: "No account managers, no offshore handoffs. You work with the senior who scopes, designs, and writes the code." },
    { tag_number: "03", title: "On-schedule delivery",
      body: "Timelines we commit to are timelines we hit. Fewer projects in flight, more attention on yours." },
    { tag_number: "04", title: "Custom-coded, no app bloat",
      body: "One-time investments instead of monthly app subscriptions. Better performance, full ownership, lower lifetime cost." },
  ],
};

const SDEV_CAPABILITIES_DEFAULTS = {
  eyebrow: "Our capabilities",
  headline_main: "Every layer of",
  headline_italic: "Shopify",
  headline_accent: "handled",
  description:
    "Nine practices, one boutique team. Senior generalists who own a discipline end-to-end — strategy, design, engineering, launch.",
  items: [
    { tag_number: "01", title: "Theme customization & UI/UX design",
      body: "Custom Shopify themes that look great, perform fast, and convert better. Responsive, brand-focused storefronts with motion, animation, and interactive detail where it matters.",
      media: "https://videos.pexels.com/video-files/3209828/3209828-hd_1920_1080_25fps.mp4" },
    { tag_number: "02", title: "Custom theme development",
      body: "Bespoke, responsive Shopify themes that combine smart design with advanced functionality. Built lean, easy to maintain, and ready to scale alongside the brand.",
      media: "https://images.unsplash.com/photo-1559028012-481c04fa702d?auto=format&fit=crop&w=1400&q=70" },
    { tag_number: "03", title: "Shopify & Shopify Plus store setup",
      body: "From full setup and configuration to advanced functionality and performance optimization — we handle every detail of a launch ready to scale.",
      media: "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=1400&q=70" },
    { tag_number: "04", title: "Theme setups & ongoing updates",
      body: "Initial installation, fine-tuning of layouts, styling, and features that reflect your brand — plus ongoing updates to keep the store fast, reliable, and visually consistent.",
      media: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1400&q=70" },
    { tag_number: "05", title: "Store optimization & audit",
      body: "Deep audits that uncover performance gaps, refine design and functionality, and ensure your store runs fast, secure, and ready to convert.",
      media: "https://videos.pexels.com/video-files/2887463/2887463-hd_1920_1080_25fps.mp4" },
    { tag_number: "06", title: "Custom features",
      body: "When Shopify's defaults aren't enough, we build custom solutions — from design enhancements to advanced integrations. One-time investments, not monthly app fees.",
      media: "https://videos.pexels.com/video-files/3141207/3141207-hd_1920_1080_25fps.mp4" },
    { tag_number: "07", title: "Maintenance & support",
      body: "Once you're live, we stay close. Flexible monthly plans for fast updates, performance work, and ongoing optimization, plus tutorials so your team owns it.",
      media: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1400&q=70" },
    { tag_number: "08", title: "Migration to Shopify",
      body: "Full migration from any platform — preserving your data, minimizing downtime, and configuring the new store for performance and growth from day one.",
      media: "https://images.unsplash.com/photo-1593642632559-0c6d3fc62b89?auto=format&fit=crop&w=1400&q=70" },
    { tag_number: "09", title: "App development & white-label",
      body: "Custom Shopify apps that automate workflows and elevate UX. Plus white-label development for agencies — our experts working invisibly under your brand.",
      media: "https://videos.pexels.com/video-files/854149/854149-hd_1920_1080_30fps.mp4" },
  ],
};

const SDEV_BUILD_SHOWCASE_DEFAULTS = {
  eyebrow: "Behind the build",
  headline_main: "We write code.",
  headline_italic: "Lots",
  headline_accent: "of it",
  description:
    "Custom Liquid, React, Hydrogen, GraphQL — written by senior engineers, reviewed on every PR. No template recycling. No app stitching.",
  video: "https://videos.pexels.com/video-files/3141207/3141207-hd_1920_1080_25fps.mp4",
  poster: "https://images.unsplash.com/photo-1517694712202-14dd9538aa97?auto=format&fit=crop&w=2000&q=70",
  stats: [
    { title: "100%", body: "Custom-coded" },
    { title: "0",    body: "Templates" },
    { title: "PR",   body: "Reviewed every push" },
  ],
};

const SDEV_POSSIBILITIES_DEFAULTS = {
  eyebrow: "Custom development · 250+ possibilities",
  headline_main: "If you can",
  headline_italic: "imagine",
  headline_accent: "build it",
  description:
    "From storefront to checkout, we build custom functionality across every layer of Shopify and Shopify Plus — with one-time coded solutions, not recurring app costs.",
  // The buckets list intentionally matches the 6 design-locked
  // mini-demos by position (0..5). Reordering will also reorder
  // which demo is paired with which bucket.
  buckets: [
    { tag_number: "01", tag_label: "Product Experience & Merchandising",
      title: "Make every product feel handpicked.",
      highlights: [
        "Product bundle kits",
        "Custom product configurators",
        "Tiered pricing rules",
        "Mix & match bundles",
        "Advanced variant selectors",
        "Gift-with-purchase triggers",
        "Custom color swatches",
        "Dynamic size guides & fit charts",
      ] },
    { tag_number: "02", tag_label: "Checkout & Cart",
      title: "Recover revenue at the last mile.",
      highlights: [
        "Upsell & cross-sell at checkout",
        "Cart goal progress bars",
        "Custom discount logic",
        "Dynamic shipping thresholds",
        "Post-purchase upsell experiences",
      ] },
    { tag_number: "03", tag_label: "Inventory & Operations",
      title: "Operate at scale without chaos.",
      highlights: [
        "Multi-warehouse product visibility",
        "Custom backorder messaging",
        "Advanced pre-order systems",
        "Real-time stock automation",
        "Gift wrapping & personalization logic",
      ] },
    { tag_number: "04", tag_label: "User Engagement & Loyalty",
      title: "Turn first-time buyers into regulars.",
      highlights: [
        "Dynamic rewards integration",
        "Custom referral flows",
        "Interactive wishlists",
        "Advanced review display systems",
        "Dynamic announcement bars",
      ] },
    { tag_number: "05", tag_label: "Marketing & Conversion",
      title: "Ship campaigns without dev tickets.",
      highlights: [
        "Custom landing page builders",
        "Campaign-specific checkout flows",
        "Discount code restrictions",
        "Tracking & analytics integrations",
        "Smart banners & personalized content",
      ] },
    { tag_number: "06", tag_label: "UI/UX Enhancements",
      title: "Storefront craft, in motion.",
      highlights: [
        "Dynamic homepage & PDP sections",
        "Sticky ATC bars & floating elements",
        "Video-based product galleries",
        "Micro-interactions & animations",
        "Custom quick-view modules",
      ] },
  ],
};

const SDEV_PROCESS_DEFAULTS = {
  eyebrow: "How we work",
  headline_main: "Seven steps,",
  headline_italic: "no",
  headline_accent: "surprises",
  description:
    "Transparent and systematic — you always see where we are, what's next, and what we're deciding right now.",
  items: [
    { tag_number: "01", title: "Discovery",
      body: "We start with your business — goals, constraints, and the metric that matters most. Nothing else gets built until that's clear." },
    { tag_number: "02", title: "Strategy",
      body: "A clear technical specification: scope, stack, milestones, and the decisions we're making before a single pixel moves." },
    { tag_number: "03", title: "Design",
      body: "UI/UX that feels like your brand, not a Shopify template. Mobile-first, motion where it earns attention." },
    { tag_number: "04", title: "Build",
      body: "Senior engineers writing custom Liquid, React, and Hydrogen — not assembling apps. Code reviews on every PR." },
    { tag_number: "05", title: "QA",
      body: "Cross-browser, cross-device, real-data testing. Performance budgets enforced before launch, not after." },
    { tag_number: "06", title: "Launch",
      body: "Coordinated cutover with zero data loss. We sit with your team on launch day until everything is stable." },
    { tag_number: "07", title: "Support",
      body: "Flexible monthly retainers for ongoing optimization, A/B testing, and the next thing on your roadmap." },
  ],
};

const SDEV_TECH_STACK_DEFAULTS = {
  eyebrow: "Stack & integrations",
  headline_main: "The tools we use,",
  headline_italic: "daily",
  items: [
    "Shopify Plus", "Liquid", "Hydrogen", "Storefront API", "React", "GraphQL",
    "TypeScript", "Tailwind", "Klaviyo", "Algolia", "Stripe", "Recharge",
    "Loop Returns", "Gorgias", "Yotpo", "Contentful",
  ].map((t) => ({ title: t })),
};

const SDEV_WHO_WE_WORK_WITH_DEFAULTS = {
  eyebrow: "Who we partner with",
  headline_part_1: "Founders ready to scale.",
  headline_italic: "eCommerce",
  headline_part_2: "teams growing fast.",
  headline_accent: "Brands",
  headline_part_3: "elevating Shopify.",
  description:
    "We build and optimize — not validate. We collaborate with teams that have a clear vision and are ready to move efficiently toward growth with tailored, results-driven solutions.",
  cta_label: "Let's talk",
};

// ============================================================
// MARKETING 360 defaults — mirror the live copy in
// /app/frontend/src/pages/MarketingPage.jsx so
// /admin/content/marketing opens fully pre-populated.
// ============================================================

const MKT_HERO_DEFAULTS = {
  breadcrumb: "eCommerce Marketing",
  eyebrow: "360 Digital Marketing",
  title_line_1: "Full circle.",
  title_line_2: "Built for",
  title_emphasis: "growth",
  title_accent: "",
  subtitle:
    "A boutique marketing team turning ideas into traffic, traffic into customers, and customers into community — strategy, creative, media, and analytics under one roof.",
  cta_primary_label: "Start a Project",
  cta_secondary_label: "See services",
  cta_secondary_href: "#services",
};

const MKT_TRUST_STRIP_DEFAULTS = {
  items: [
    { title: "In-house team" },
    { title: "Data-led" },
    { title: "360° stack" },
    { title: "10+ years" },
    { title: "Brooklyn · Remote" },
  ],
};

const MKT_WHY_US_DEFAULTS = {
  eyebrow: "The Sunday way",
  headline_main: "Strategy meets",
  headline_italic: "execution",
  headline_middle: "",
  headline_accent: "Data",
  headline_tail: "meets craft.",
  columns: [
    { title: "The plan, never the gimmick.",
      body: "Data-driven strategies that align with your business goals and audience behaviour. Every move is built for measurable growth and long-term impact — actionable, not aspirational." },
    { title: "Channels that compound.",
      body: "Email, paid, organic, and creative — calibrated together so each channel fuels the next. Content that informs, inspires, and sells. Campaigns built to be measured, not just launched." },
  ],
};

const MKT_METHOD_DEFAULTS = {
  intro_eyebrow: "Our method",
  headline_main: "Six steps,",
  headline_italic: "measured",
  headline_accent: "every week",
  intro_description:
    "The way we run every engagement — same rhythm whether we're shipping a 2-week campaign or a multi-quarter growth program.",
  kpi_label: "What ships at this stage",
  items: [
    { tag_number: "01", tag_label: "Listen",
      title: "We learn your business before we build for it.",
      body: "Workshops, audits, customer interviews, analytics deep-dives. Nothing gets shipped until we understand what's actually moving the needle.",
      highlights: ["Audit & analytics", "Audience research", "Goal alignment"] },
    { tag_number: "02", tag_label: "Strategize",
      title: "A roadmap with milestones, not just opinions.",
      body: "Channel mix, budget allocation, content calendar, and the experiments we'll run. Decisions documented before a dollar is spent.",
      highlights: ["Channel strategy", "Budget plan", "Test roadmap"] },
    { tag_number: "03", tag_label: "Create",
      title: "Creative that earns the click — and the second one.",
      body: "Copy, design, video, and motion built around the strategy. No template ads, no recycled hooks. Made-for-channel from the start.",
      highlights: ["Ad creative", "Email design", "Landing pages"] },
    { tag_number: "04", tag_label: "Launch",
      title: "Coordinated rollouts across every active channel.",
      body: "Paid, organic, email, influencer, lifecycle — orchestrated to fire together. Tracking and attribution wired in from day one.",
      highlights: ["Cross-channel ops", "Tracking setup", "QA pre-launch"] },
    { tag_number: "05", tag_label: "Measure",
      title: "Performance dashboards your team can actually read.",
      body: "Weekly reviews, monthly deep-dives. We focus on the numbers that compound — LTV, CAC, return rate, repeat purchase — not vanity reach.",
      highlights: ["Weekly reporting", "LTV / CAC", "Channel attribution"] },
    { tag_number: "06", tag_label: "Iterate",
      title: "Optimize, double down, kill what isn't working.",
      body: "We run our own experiments and report results candidly. The plan changes — what doesn't is the discipline of measuring before deciding.",
      highlights: ["A/B testing", "Creative refresh", "Quarterly review"] },
  ],
};

const MKT_SERVICES_GRID_DEFAULTS = {
  eyebrow: "Eight disciplines, one team",
  headline_main: "Every channel",
  headline_italic: "that",
  headline_accent: "moves metrics",
  description:
    "Strategy, creative, and media — calibrated together so each channel makes the next one work harder.",
  cta_href: "#contact",
  items: [
    { tag_number: "01", title: "Strategy & Consulting",
      body: "Data-driven strategies that align with your business goals — every move built for measurable growth and long-term impact.",
      media: "https://images.unsplash.com/photo-1551836022-d5d88e9218df?auto=format&fit=crop&w=1200&q=70" },
    { tag_number: "02", title: "Content Creation",
      body: "Engaging product visuals, copy, and digital assets that enhance the buying journey and boost conversions.",
      media: "https://images.unsplash.com/photo-1499951360447-b19be8fe80f5?auto=format&fit=crop&w=1200&q=70" },
    { tag_number: "03", title: "Social Media",
      body: "Strategic content calendars and targeted campaigns that turn followers into loyal customers and revenue.",
      media: "https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?auto=format&fit=crop&w=1200&q=70" },
    { tag_number: "04", title: "Email Marketing",
      body: "Targeted, automated, personalized flows — from welcome series to lifecycle. Segmentation that maximizes engagement.",
      media: "https://images.unsplash.com/photo-1633265486064-086b219458ec?auto=format&fit=crop&w=1200&q=70" },
    { tag_number: "05", title: "Influencer & Collabs",
      body: "Authentic voice partnerships that boost engagement, build trust, and convert followers into loyal customers.",
      media: "https://images.unsplash.com/photo-1542038784456-1ea8e935640e?auto=format&fit=crop&w=1200&q=70" },
    { tag_number: "06", title: "Paid Marketing",
      body: "Targeted Meta, TikTok, Pinterest, Google Search & Shopping campaigns — precise audiences, optimized creatives, measurable ROI.",
      media: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=1200&q=70" },
    { tag_number: "07", title: "SEO",
      body: "Comprehensive audits and competitive analysis — optimize every stage of the funnel and drive qualified traffic that converts.",
      media: "https://images.unsplash.com/photo-1432888622747-4eb9a8efeb07?auto=format&fit=crop&w=1200&q=70" },
    { tag_number: "08", title: "Conversion Optimization",
      body: "User-behaviour analysis, iterative testing, and journey optimization. Turn more visitors into buyers with data-driven changes.",
      media: "https://images.unsplash.com/photo-1543286386-713bdd548da4?auto=format&fit=crop&w=1200&q=70" },
  ],
};

const MKT_SHOWREEL_DEFAULTS = {
  eyebrow: "Behind the work",
  headline_main: "We move metrics —",
  headline_italic: "not",
  headline_accent: "vanity numbers",
  description:
    "Strategy, creative, paid media, and analytics calibrated together so the numbers that matter — LTV, CAC, repeat purchase, return rate — actually compound.",
  video: "https://videos.pexels.com/video-files/3209828/3209828-hd_1920_1080_25fps.mp4",
  poster: "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=2000&q=70",
};

// ============================================================
// CREATIVE & DESIGN defaults — mirror live copy in CreativePage.jsx
// ============================================================

const CRE_HERO_DEFAULTS = {
  breadcrumb: "Creative & Design",
  eyebrow: "Ecommerce design · UI / UX · Brand systems",
  video: "https://videos.pexels.com/video-files/3141207/3141207-hd_1920_1080_25fps.mp4",
  poster: "https://images.unsplash.com/photo-1492691527719-9d1e07e534b4?auto=format&fit=crop&w=2000&q=70",
  title_line_1: "We design stores",
  title_line_2: "built to",
  verbs: [
    { title: "Connect" },
    { title: "Captivate" },
    { title: "Convert" },
  ],
  subtitle:
    "Responsive UI, optimized visual systems, and high-impact creative for ecommerce — tailored to enhance usability, streamline interaction, and elevate every brand touchpoint.",
  cta_primary_label: "Get a free consultation",
  cta_secondary_label: "See solutions",
  cta_secondary_href: "#tailored",
  side_caption: "Reel · 2025 / 26",
  marquee_words: [
    { title: "Connect" }, { title: "Captivate" }, { title: "Convert" },
    { title: "Connect" }, { title: "Captivate" }, { title: "Convert" },
  ],
};

const CRE_TAILORED_SOLUTIONS_DEFAULTS = {
  progress_label: "Reel — Tailored solutions",
  items: [
    { tag_number: "01", tag_label: "Tailored Solutions",
      title: "Shopify Plus, custom-built",
      body: "Designs built for brands that demand more — more power, more flexibility, more results. Tailored features, seamless integrations, and advanced functionality to handle whatever you throw at it.",
      tag: "No limits, just results.",
      media: "https://images.unsplash.com/photo-1556761175-b413da4baf72?auto=format&fit=crop&w=1800&q=70" },
    { tag_number: "02", tag_label: "Tailored Solutions",
      title: "Shopify website design",
      body: "Look sharp and sell hard — pixel-perfect layouts, smart typography, and color systems that don't just match your brand, they amplify it. Built for engagement.",
      tag: "Beautiful. Intuitive. Built to move product.",
      media: "https://images.unsplash.com/photo-1545239351-cefce43f1e73?auto=format&fit=crop&w=1800&q=70" },
    { tag_number: "03", tag_label: "Tailored Solutions",
      title: "UX design",
      body: "UX is how your store works. We design seamless, intuitive shopping experiences that guide users from entry to checkout — fast. Smart structure, smooth navigation, zero guesswork.",
      tag: "Designing the path to purchase.",
      media: "https://images.unsplash.com/photo-1522542550221-31fd19575a2d?auto=format&fit=crop&w=1800&q=70" },
    { tag_number: "04", tag_label: "Tailored Solutions",
      title: "Interaction design",
      body: "Beyond aesthetics — high-performing, ecommerce-optimized interfaces with precision layouts, scalable components, and responsive design at the core. Every pixel engineered to keep users engaged.",
      tag: "Precision UI for high-performance stores.",
      media: "https://images.unsplash.com/photo-1581291518633-83b4ebd1d83e?auto=format&fit=crop&w=1800&q=70" },
  ],
};

const CRE_STATS_BAND_DEFAULTS = {
  eyebrow: "Our vision",
  headline_main: "A decade of",
  headline_italic: "brand",
  headline_accent: "building",
  description:
    "Powerful, conversion-focused design — built once, ready to use, made to work beautifully together. These are just the numbers; the work speaks louder.",
  items: [
    { title: "10", body: "Years of brand building", suffix: "+" },
    { title: "261", body: "Projects completed", suffix: "" },
    { title: "15", body: "Team members", suffix: "" },
  ],
};

const CRE_POSSIBILITIES_LAB_DEFAULTS = {
  eyebrow: "Possibilities Lab",
  headline_main: "Smart selling",
  headline_italic: "starts",
  headline_accent: "here",
  description:
    "Conversion-focused features built once and made to work beautifully together. A few examples below — if you can dream it, we can build it. Hover to play.",
  items: [
    { tag_number: "01", title: "Gift with purchase",
      body: "A smart sales enhancer designed to drive conversions by offering perceived value, increasing AOV, and encouraging repeat purchases.",
      media: "https://images.unsplash.com/photo-1607082348824-0a96f2a4b9da?auto=format&fit=crop&w=1200&q=70",
      video: "https://videos.pexels.com/video-files/4622417/4622417-uhd_2560_1440_25fps.mp4",
      span: "lg:col-span-5 lg:row-span-2" },
    { tag_number: "02", title: "Bundling",
      body: "A smooth, interactive bundle builder — customize and preview product sets in real time with dynamic pricing and one-click cart.",
      media: "https://images.unsplash.com/photo-1483985988355-763728e1935b?auto=format&fit=crop&w=1200&q=70",
      video: "https://videos.pexels.com/video-files/4068314/4068314-uhd_2560_1440_25fps.mp4",
      span: "lg:col-span-7" },
    { tag_number: "03", title: "Quiz & personality test",
      body: "Multi-step UI flow that blends sleek UX with logic-driven personalization — ending in a tailored product suggestion.",
      media: "https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&w=1200&q=70",
      video: "https://videos.pexels.com/video-files/3209663/3209663-uhd_2560_1440_25fps.mp4",
      span: "lg:col-span-4" },
    { tag_number: "04", title: "Variant swatch",
      body: "Real-time preview of how a product looks in any option — click to switch, no reload, no friction.",
      media: "https://images.unsplash.com/photo-1485518882345-15568b007407?auto=format&fit=crop&w=1200&q=70",
      video: "https://videos.pexels.com/video-files/4109016/4109016-uhd_2560_1440_25fps.mp4",
      span: "lg:col-span-3" },
    { tag_number: "05", title: "Upsell configurator",
      body: "Cross-sell strategy in your control — recommended products dynamically loaded, complementary items added straight from cart.",
      media: "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?auto=format&fit=crop&w=1200&q=70",
      video: "https://videos.pexels.com/video-files/3209211/3209211-uhd_2560_1440_25fps.mp4",
      span: "lg:col-span-7 lg:row-span-2" },
    { tag_number: "06", title: "Product configurator",
      body: "Smooth, interactive Shopify setup — instant updates, zero reloads, every screen size handled. Optimized to guide users from interest to purchase.",
      media: "https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=1200&q=70",
      video: "https://videos.pexels.com/video-files/3015527/3015527-uhd_2732_1440_25fps.mp4",
      span: "lg:col-span-5" },
  ],
};

const CRE_MORE_SERVICES_DEFAULTS = {
  eyebrow: "More services we're great at",
  headline_main: "Beyond the build —",
  headline_italic: "an",
  headline_accent: "entire growth stack",
  items: [
    { tag_number: "01", tag_label: "Email marketing",
      title: "Your most overlooked growth channel — optimized.",
      body: "From planning to post-launch, end-to-end campaigns powered by segmentation, automation, and personalization. Built to drive engagement, conversions, and long-term loyalty — not just inbox placement.",
      cta: "Discover solutions",
      media: "https://images.unsplash.com/photo-1633265486064-086b219458ec?auto=format&fit=crop&w=1600&q=70" },
    { tag_number: "02", tag_label: "Social ads",
      title: "Noise isn't the goal — impact is.",
      body: "No two platforms are the same and neither are our strategies. Creative and targeting tailored to each channel, rapid experimentation, multivariate testing, and data-backed decisions that compound ROI.",
      cta: "Learn more",
      media: "https://images.unsplash.com/photo-1611162617474-5b21e879e113?auto=format&fit=crop&w=1600&q=70" },
    { tag_number: "03", tag_label: "Content creation",
      title: "Connect, convert, and cultivate loyal customers.",
      body: "Social posts, blog articles, on-site merchandising — every asset crafted with purpose. Full-funnel storytelling that combines culture, data, and brand voice to keep you relevant and consistently engaging.",
      cta: "Discover more",
      media: "https://images.unsplash.com/photo-1499951360447-b19be8fe80f5?auto=format&fit=crop&w=1600&q=70" },
    { tag_number: "04", tag_label: "Multimedia",
      title: "Visual storytelling that sells.",
      body: "Professional photography, film, and motion — from website content and product hero shots to dynamic social videos. High-quality visuals built to captivate and convert across every channel.",
      cta: "Contact us",
      media: "https://images.unsplash.com/photo-1492691527719-9d1e07e534b4?auto=format&fit=crop&w=1600&q=70" },
  ],
};

const CRE_LATEST_PROJECTS_DEFAULTS = {
  eyebrow: "Latest projects",
  headline_main: "The receipts.",
  headline_italic: "In one",
  headline_accent: "place",
  cta_label: "All projects",
  cta_href: "#contact",
  items: [
    { tag_number: "01", title: "Café Joyeux", body: "Coffee on a mission", href: "#",
      media: "https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?auto=format&fit=crop&w=1400&q=75" },
    { tag_number: "02", title: "Veronique Gabai", body: "Perfume & fragrances", href: "#",
      media: "https://images.unsplash.com/photo-1541643600914-78b084683601?auto=format&fit=crop&w=1400&q=75" },
    { tag_number: "03", title: "Madam Glam", body: "Gel & nail polish, art", href: "#",
      media: "https://images.unsplash.com/photo-1604654894610-df63bc536371?auto=format&fit=crop&w=1400&q=75" },
    { tag_number: "04", title: "Ming Yu Wang", body: "Designer statement jewelry", href: "#",
      media: "https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?auto=format&fit=crop&w=1400&q=75" },
  ],
};

const CRE_CROSS_BROWSER_DEFAULTS = {
  eyebrow: "Browse the studio",
  headline_main: "Where would you",
  headline_italic: "like to",
  headline_accent: "go next",
  description: "Same studio, different lens. Pick your path — or jump straight to a conversation.",
  items: [
    { title: "Sunday.", body: "Digital Solutions", href: "/",
      media: "https://images.unsplash.com/photo-1614728263952-84ea256f9679?auto=format&fit=crop&w=1200&q=70",
      span: "lg:col-span-4 lg:row-span-2", is_cta: "" },
    { title: "Ecommerce", body: "Powering Stores", href: "/shopify-experts-development",
      media: "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&w=1200&q=70",
      span: "lg:col-span-4", is_cta: "" },
    { title: "Marketing", body: "Streamlined Growth", href: "/ecommerce-marketing-agency",
      media: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=1200&q=70",
      span: "lg:col-span-4", is_cta: "" },
    { title: "Showcase", body: "Vision. Action. Result.", href: "/",
      media: "https://images.unsplash.com/photo-1542038784456-1ea8e935640e?auto=format&fit=crop&w=1200&q=70",
      span: "lg:col-span-4", is_cta: "" },
    { title: "Let's talk", body: "And Build What's Next", href: "#contact",
      media: "https://images.unsplash.com/photo-1481487196290-c152efe083f5?auto=format&fit=crop&w=1200&q=70",
      span: "lg:col-span-4", is_cta: "yes" },
  ],
};

// ============================================================
// ABOUT / OUR STORY defaults — mirror AboutPage.jsx live copy
// ============================================================

const ABT_HERO_DEFAULTS = {
  breadcrumb: "Our Story",
  eyebrow: "About — Est. 2014 · Brooklyn / Remote",
  title_line_1: "Our",
  title_line_2: "Story",
  subtitle:
    "Two teams. Twenty-five years of combined craft. One studio built on cosmetics, fashion, and lifestyle ecommerce — with a stack that runs end-to-end from UI/UX and Shopify development to creative, content, and paid media.",
  cta_primary_label: "Get in touch",
  cta_secondary_label: "Read the story",
  cta_secondary_href: "#story",
  photo_back:  "https://images.unsplash.com/photo-1521737852567-6949f3f9f2b5?auto=format&fit=crop&w=900&q=70",
  photo_front: "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=900&q=70",
  badge_number: "+",
  badge_caption: "Two · One",
  marquee_words: [
    { title: "Cosmetics" }, { title: "Fashion" }, { title: "Lifestyle" },
    { title: "Shopify" }, { title: "Brand systems" }, { title: "Creative" },
    { title: "Cosmetics" }, { title: "Fashion" }, { title: "Lifestyle" },
    { title: "Shopify" }, { title: "Brand systems" }, { title: "Creative" },
  ],
};

const ABT_ORIGIN_DEFAULTS = {
  eyebrow: "The origin",
  headline_main: "Two teams.",
  headline_italic: "One",
  headline_accent: "studio",
  sidebar_description:
    "How Sunday Digital came to be — and what we're built to ship every working day since.",
  intro_part_1:
    "Sunday Digital is the result of two experienced teams coming together —",
  intro_italic: "a combined",
  intro_accent: "25+ years",
  intro_part_2:
    " of craft in web development and digital marketing, sharpened on the cosmetics, fashion, and lifestyle industries.",
  paragraph_1:
    "The merger let us become a true full-service studio — UI/UX, Shopify ecommerce development, SEO, creative, content and email marketing, social and search advertising, social media management, and everything that orbits a brand's growth.",
  paragraph_2:
    "Today we run as one team — collaborators across UX/UI design, web development, creative services, digital marketing, and social media — shipping the work that ends up under each brand's roof.",
  stat_value: "500",
  stat_suffix: "+",
  stat_label: "Successfully completed tasks since day one — and counting.",
};

const ABT_STATS_RIBBON_DEFAULTS = {
  items: [
    { title: "25",  body: "Years of combined craft",       suffix: "+" },
    { title: "500", body: "Tasks completed",               suffix: "+" },
    { title: "2",   body: "Teams merged into one studio",  suffix: "→1" },
    { title: "10",  body: "Disciplines under one roof",    suffix: "+" },
  ],
};

const ABT_TRUST_DEFAULTS = {
  eyebrow: "Our valued partners — the index",
  headline_main: "They",
  headline_italic: "trust",
  headline_accent: "us",
  description:
    "Brands that ship with us — across cosmetics, fashion, food, and lifestyle. Hover any partner to see the work.",
  caption_left: "Partner",
  caption_right: "Discipline · Year",
  footer_right: "Selected work · 2018 — 2025",
  preview_caption: "Hover the index — the work follows.",
  items: [
    { title: "Veronique Gabai", body: "Brand world & flagship Shopify Plus build.", discipline: "Fragrance", year: "2019",
      media: "https://images.unsplash.com/photo-1541643600914-78b084683601?auto=format&fit=crop&w=1400&q=70" },
    { title: "Café Joyeux", body: "Multi-locale storefront with cause-led editorial.", discipline: "Food & Mission", year: "2021",
      media: "https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?auto=format&fit=crop&w=1400&q=70" },
    { title: "Madam Glam", body: "Performance redesign, +38% conversion.", discipline: "Beauty · Nail", year: "2018",
      media: "https://images.unsplash.com/photo-1604654894610-df63bc536371?auto=format&fit=crop&w=1400&q=70" },
    { title: "Sclarandis", body: "Identity refresh + content engine.", discipline: "Lifestyle", year: "2022",
      media: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1400&q=70" },
    { title: "Ming Yu Wang", body: "Editorial commerce for sculptural pieces.", discipline: "Designer Jewelry", year: "2020",
      media: "https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?auto=format&fit=crop&w=1400&q=70" },
    { title: "Swand", body: "Subscription-first DTC launch.", discipline: "Lifestyle", year: "2023",
      media: "https://images.unsplash.com/photo-1556228720-195a672e8a03?auto=format&fit=crop&w=1400&q=70" },
    { title: "Nail Reserve", body: "Full digital storefront from kickoff to launch.", discipline: "Beauty · Salon", year: "2024",
      media: "https://images.unsplash.com/photo-1604902396830-aca29e19b067?auto=format&fit=crop&w=1400&q=70" },
    { title: "Bow & Arrow", body: "Lookbook-grade product storytelling.", discipline: "Fashion", year: "2022",
      media: "https://images.unsplash.com/photo-1483985988355-763728e1935b?auto=format&fit=crop&w=1400&q=70" },
  ],
};

const ABT_HOW_WE_DEFAULTS = {
  eyebrow: "How we do it",
  headline_main: "Six tenets.",
  headline_italic: "No",
  headline_accent: "compromises",
  description:
    "The principles every project runs through — from the first kickoff conversation to the final post-launch review.",
  items: [
    { tag_number: "01", tag_label: "Customer satisfaction",
      title: "Your success is our success.",
      body: "Clear communication, transparency, and responsiveness through the entire project lifecycle. We measure ourselves on whether you'd ship with us again.",
      credo: "Happy clients. Repeat clients.",
      media: "https://images.unsplash.com/photo-1521737852567-6949f3f9f2b5?auto=format&fit=crop&w=1400&q=70" },
    { tag_number: "02", tag_label: "Results-driven",
      title: "Measurable outcomes, not vanity metrics.",
      body: "Whether it's traffic, conversion rate, or brand visibility — every brief defines what 'won' looks like before we start. Then we go and win it.",
      credo: "If it can't be measured, it doesn't ship.",
      media: "https://images.unsplash.com/photo-1543286386-713bdd548da4?auto=format&fit=crop&w=1400&q=70" },
    { tag_number: "03", tag_label: "Collaboration",
      title: "We design with you, not for you.",
      body: "We listen first. Goals, constraints, taste — we soak them up. Strategies are built alongside our clients, not handed to them, so the work feels owned and lasts.",
      credo: "Best ideas win — wherever they come from.",
      media: "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=1400&q=70" },
    { tag_number: "04", tag_label: "Efficiency",
      title: "Done well. Done on time.",
      body: "Streamlined process, agile cadence, the right tools. Projects finish on time and on budget because the system was designed for it — without compromising the work.",
      credo: "Speed without shortcuts.",
      media: "https://images.unsplash.com/photo-1519389950473-47ba0277781c?auto=format&fit=crop&w=1400&q=70" },
    { tag_number: "05", tag_label: "Innovation",
      title: "Ahead of the curve, always.",
      body: "We track the platforms, the tools, the patterns — so you don't have to. Cutting-edge isn't the goal; cutting-edge that solves your problem is.",
      credo: "New for new's sake is dead. Useful is forever.",
      media: "https://images.unsplash.com/photo-1581291518633-83b4ebd1d83e?auto=format&fit=crop&w=1400&q=70" },
    { tag_number: "06", tag_label: "Expertise",
      title: "Seasoned operators. Specialists, not generalists.",
      body: "From Shopify Plus engineering to performance marketing, every discipline is led by someone who's done it for a decade. Real range, no posturing.",
      credo: "The team you wish you'd hired earlier.",
      media: "https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&w=1400&q=70" },
  ],
};

const ABT_MANIFESTO_DEFAULTS = {
  eyebrow: "The promise",
  quote_part_1: "Choose Sunday for",
  italic_1: "unparalleled",
  quote_part_2: "",
  accent_phrase: "expertise",
  quote_part_3: ", innovation, efficiency, collaboration — and an",
  italic_2: "unwavering",
  quote_part_4: "commitment to results.",
  cta_primary_label: "Start a project",
  cta_secondary_label: "See the work",
  cta_secondary_href: "/creative",
};

// --------------------------- CONTACT ---------------------------
const CONTACT_HERO_DEFAULTS = {
  breadcrumb: "Contact",
  eyebrow: "Currently booking · Q3 2026",
  title_line_1: "Let's",
  title_line_2: "talk",
  subtitle:
    "Pick the path that fits — a 30-minute intro call, a written brief, or a direct line to the right team. Whichever you choose, we'll reply within 24 hours on weekdays.",
  cta_label: "Send a brief",
};

const CONTACT_ROUTING_DEFAULTS = {
  eyebrow: "Pick a path",
  headline_main: "Choose your",
  headline_italic: "doorway",
  headline_accent: "in",
  description:
    "Every message gets to the right desk. Tell us what you're here for and we'll route you to the right team.",
  items: [
    { tag_label: "New project", title: "Start a project",
      body: "Brief us on what you're building. We'll come back with a scope, a team, and a starting line.",
      cta_label: "Send a brief", href: "", is_primary: true, icon: "Sparkles" },
    { tag_label: "Careers", title: "Join the studio",
      body: "Designers, engineers, strategists, producers. Tell us what you do — and what you want to build next.",
      cta_label: "Apply now", href: "mailto:careers@sundaydigital.io", is_primary: false, icon: "Briefcase" },
    { tag_label: "Partnerships", title: "Build with us",
      body: "Tooling, platforms, agency collaborations. If your work touches ours, we want to hear from you.",
      cta_label: "Get in touch", href: "mailto:partnerships@sundaydigital.io", is_primary: false, icon: "Handshake" },
  ],
};

const CONTACT_BOOK_DEFAULTS = {
  visible: false,
  eyebrow: "Direct booking",
  headline_main: "30 minutes,",
  headline_italic: "no",
  headline_accent: "pitch deck",
  body:
    "A working call. Bring the goal, the constraint, the deadline. We'll bring the questions and an honest read on whether we're a fit.",
  bullets: [
    { title: "Free, no commitment" },
    { title: "Real founders / leads on the call" },
    { title: "You leave with a written summary" },
  ],
  embed_url: "https://cal.com/sundaydigital/intro",
  fallback_caption: "Trouble with the embed?",
  fallback_cta_label: "Send a brief instead ↗",
};

const CONTACT_FAQ_DEFAULTS = {
  eyebrow: "Frequently asked",
  headline_main: "Before the",
  headline_italic: "first",
  headline_accent: "call",
  description:
    "The questions every client asks before they book. Read first; ask second.",
  items: [
    { title: "What does a typical engagement cost?",
      body: "Most projects fall between $40k and $250k+ depending on scope. Retainers start at $12k/month for ongoing partnerships. We share a transparent rate card and a fixed-scope estimate after the intro call." },
    { title: "How long until we ship?",
      body: "A standard Shopify Plus build takes 10–14 weeks from kickoff to launch. Smaller scopes (theme refresh, single-feature builds) ship in 3–6 weeks. We commit to dates in writing, not just deadlines on Slack." },
    { title: "Do you work with brands outside the US?",
      body: "Yes — we run a remote-first studio with active partnerships across the US, EU, and UK. Most calls happen between 9am–4pm EST, but we adjust for client timezone where it matters." },
    { title: "What's your process?",
      body: "Six tenets — Listen, Strategize, Create, Launch, Measure, Iterate. The first kickoff is a workshop. The first deliverable is a roadmap, not a deck. From there, weekly demos and monthly business reviews. Read the full method on our About page." },
    { title: "What if we already have a Shopify store?",
      body: "Most of our work is on existing stores. We start with an audit (technical, UX, performance, conversion), surface the highest-leverage opportunities, and ship them in priority order. No 'rip and replace' unless the architecture demands it." },
    { title: "Do you offer one-off projects, retainers, or both?",
      body: "Both. Most clients start with a defined project (a build, a redesign, a migration) and convert to a retainer after launch — because growth work is continuous. We're transparent about which model fits which moment." },
  ],
};

const CONTACT_DETAILS_DEFAULTS = {
  visible: true,
  eyebrow: "Direct line",
  headline_main: "Old-fashioned",
  headline_italic: "still",
  headline_accent: "works",
  email: "hello@sundaydigital.io",
  socials: [
    { title: "Instagram", body: "https://instagram.com/sundaydigital" },
    { title: "LinkedIn", body: "https://linkedin.com/company/sundaydigital" },
    { title: "Dribbble", body: "https://dribbble.com/sundaydigital" },
  ],
  offices: [
    { title: "Brooklyn HQ", body: "123 Front Street", addr2: "Brooklyn, NY 11201", note: "By appointment" },
    { title: "Remote · EU", body: "Distributed team", addr2: "Paris · Lisbon · Berlin", note: "9am–6pm CET" },
    { title: "Hours", body: "Mon — Fri", addr2: "9am — 6pm EST", note: "Reply ≤ 24h" },
  ],
};

const CONTACT_CLOSING_DEFAULTS = {
  eyebrow: "Or just go for it",
  headline_main: "Got the brief?",
  headline_italic: "Send it.",
  headline_accent: "We'll read it today",
  cta_label: "Send a brief",
};

const CONTACT_FORM_DEFAULTS = {
  eyebrow: "The Brief",
  reply_note: "Reply within 24h",
  headline_main: "Tell us about",
  headline_italic: "your",
  headline_accent: "project",
  lead: "Walk us through what you're building. The more we know, the sharper our first response.",
  label_service: "What you need",
  label_scope: "Scope",
  label_about: "About you",
  label_brief: "The brief",
  ph_name: "Your name",
  ph_email: "you@company.com",
  ph_company: "Optional",
  ph_website: "https://",
  ph_brief: "Walk us through it…",
  submit_label: "Send the brief",
  submit_loading: "Sending…",
  required_legend: "Required",
  footer_a: "250+ stores",
  footer_b: "10+ years",
  footer_c: "Brooklyn-based",
  contact_email_label: "hello@sundaydigital.io",
  contact_email_href: "",
  contact_social_label: "@sundaydigital",
  contact_social_href: "https://instagram.com/sundaydigital",
  req_service: true,
  req_scope: false,
  req_name: true,
  req_email: true,
  req_company: false,
  req_website: false,
  req_brief: true,
};

// --------------------------- SHOWCASE (listing page) ---------------------------
const SHOWCASE_HERO_DEFAULTS = {
  breadcrumb: "Showcase",
  eyebrow: "Selected work · {count} {stories}",
  title_lead: "Show",
  title_italic: "case",
  subtitle:
    "We love what we do and the clients we work with. Each project handled with efficiency — exceptional results every time, and long-term partnerships built along the way.",
  cta_primary_label: "Start a project",
  cta_secondary_label: "See the work",
  cta_secondary_href: "#projects",
};

const SHOWCASE_MARQUEE_DEFAULTS = {
  use_auto: true,
  items: [],
};

const SHOWCASE_CLOSING_DEFAULTS = {
  eyebrow: "The work continues",
  headline_lead: "{count_word_cap} stories.",
  headline_italic: "Yours could be",
  headline_accent: "{next_word}",
  cta_primary_label: "Start a project",
  cta_secondary_label: "Read the studio story",
  cta_secondary_href: "/about",
};

// --------------------------- LEGAL PAGES ---------------------------
export const PRIVACY_DEFAULTS = {
  header: {
    breadcrumb: "Privacy Policy",
    eyebrow: "Legal",
    title: "Privacy Policy",
    last_updated: "May 2, 2026",
  },
  body: {
    markdown: `## Overview

This Privacy Policy describes how Sunday Digital ("we", "us", "our") collects, uses, and shares information when you visit sundaydigital.io or engage our services.

## Information we collect

We collect information you provide directly — your name, email address, company, and the content of any messages you send us through the Contact form on this site. We also receive standard server logs: your IP address, browser type, pages viewed, and timestamps.

## How we use it

- Replying to your inquiry
- Scoping and delivering work for clients we engage with
- Understanding how the site is used so we can improve it
- Sending you transactional emails related to active engagements

We do **not** sell your personal information.

## Cookies

We use a small number of essential cookies for session state and analytics. You can disable cookies in your browser; some site features may stop working.

## Sharing

We share information with sub-processors only when strictly required to operate (hosting, email delivery, analytics). Each sub-processor is bound by contract and applicable data-protection law.

## Your rights

If you are in the EU, UK, or California, you have the right to access, correct, export, or delete your personal information. Email us at hello@sundaydigital.io and we will respond within 30 days.

## Contact

Questions about this policy? Email **hello@sundaydigital.io**.
`,
  },
};

export const TERMS_DEFAULTS = {
  header: {
    breadcrumb: "Terms & Conditions",
    eyebrow: "Legal",
    title: "Terms & Conditions",
    last_updated: "May 2, 2026",
  },
  body: {
    markdown: `## Acceptance

By accessing sundaydigital.io or engaging Sunday Digital LLC for services, you agree to these Terms.

## Services

We provide design, development, marketing, and creative services on a per-engagement basis. Specific scope, deliverables, timelines, and fees are documented in the Statement of Work signed by both parties.

## Intellectual property

- The content on this site (text, images, code) is owned by Sunday Digital LLC unless otherwise noted.
- For client engagements, ownership of deliverables transfers upon final payment as documented in the SOW.
- Our internal tools, methods, and pre-existing IP remain ours.

## Confidentiality

We treat your roadmap, data, and proprietary information as confidential. We can share publicly only with your written approval.

## Liability

To the fullest extent permitted by law, our total liability is capped at the fees paid for the specific engagement giving rise to the claim. We are not liable for indirect, consequential, or punitive damages.

## Governing law

These Terms are governed by the laws of the State of New York, without regard to conflicts-of-law principles.

## Contact

Questions about these terms? Email **hello@sundaydigital.io**.
`,
  },
};

// --------------------------- GLOBALS (Nav + Footer) ---------------------------
const NAV_DEFAULTS = {
  logo: "https://customer-assets.emergentagent.com/job_shopify-ux-revamp/artifacts/lime8iqi_logo-web.png",
  cta_label: "Get Started",
  cta_action: "open_contact",
  cta_href: "",
  overlay_pages: [
    { title: "Home", body: "Where it all starts", href: "/", route: true },
    { title: "Shopify Development", body: "Themes, apps, migrations, custom builds", href: "/shopify-experts-development", route: true },
    { title: "Marketing 360", body: "Growth, performance & brand", href: "/ecommerce-marketing-agency", route: true },
    { title: "Creative & Design", body: "Editorial, motion, multimedia", href: "/creative", route: true },
    { title: "Conversion Optimization", body: "CRO woven through every build", href: "/conversion-optimization", route: true },
    { title: "Showcase", body: "Selected work · 11 stories", href: "/showcase", route: true },
    { title: "Our Story", body: "Two teams, one studio", href: "/about", route: true },
    { title: "Contact", body: "Book a call · send a brief", href: "/contact", route: true },
    { title: "Blog", body: "Insights & playbooks", href: "/blog", route: true },
  ],
  footer_email: "hello@sundaydigital.io",
  footer_tagline: "New York · Remote",
  show_variants: true,
};

const FOOTER_DEFAULTS = {
  logo: "",
  tagline:
    "One partner. Every Shopify need. Design, development, marketing — in-house.",
  columns: [
    { title: "Services",
      links: "Development | /shopify-experts-development\nMarketing | /ecommerce-marketing-agency\nCreative | /creative\nConversion Optimization | /conversion-optimization\nMigration | #contact" },
    { title: "Company",
      links: "Showcase | /showcase\nOur Story | /about\nFAQ | /contact#faq\nContact | #contact" },
    { title: "Get in touch",
      links: "hello@sundaydigital.io | mailto:hello@sundaydigital.io\nBook a call | #contact" },
  ],
  copyright: "© {year} Sunday Digital. All rights reserved.",
  legal_links: [
    { title: "Privacy", body: "/privacy" },
    { title: "Terms", body: "/terms" },
  ],
};

/**
 * Given the raw `data` blob for a page and its key, return the data
 * with any MISSING sections pre-filled from the live-site snapshot.
 * Existing values always win — we only fill gaps.
 */
// =============================================================
// HOMEPAGE REVIEW — full defaults mirror of the public copy.
// Each list (trust_items, points, cards, bullets, steps, etc.)
// has its first-open default that the editor will seed if the
// admin hasn't saved anything yet. Once they touch the list, the
// admin's version always wins.
// =============================================================
export const HR_HERO_DEFAULTS = {
  breadcrumb: "Private Homepage Review",
  eyebrow: "By invitation · selective",
  title_line_1: "Private Shopify",
  title_line_2: "homepage",
  title_accent: "review",
  subtitle:
    "See how your existing Shopify homepage could become clearer, more premium, and more conversion-focused.",
  body:
    "Submit your Shopify store for a private homepage review. If we see a clear opportunity to improve your homepage direction, UX, and conversion flow, Sunday Digital may prepare a focused homepage preview — with no initial commitment.",
  cta_label: "Request a Homepage Review",
  trust_items: [
    { title: "For existing Shopify stores only" },
    { title: "Reviewed manually" },
    { title: "No initial commitment" },
  ],
};

export const HR_WHY_DEFAULTS = {
  eyebrow: "Why this exists",
  headline_main: "A clearer way to explore",
  headline_italic: "what's possible.",
  body_1:
    "Not every Shopify brand is ready to jump into a full redesign, CRO project, or long-term development partnership immediately. This review gives you a focused way to understand where your homepage experience may be holding back clarity, trust, and conversion.",
  body_2:
    "For the right stores, we may prepare a private homepage preview showing how Sunday Digital would approach the structure, hierarchy, and overall direction of your homepage.",
  points: [
    { title: "Strategic review of your current Shopify homepage" },
    { title: "Focus on UX, brand perception, clarity, and conversion flow" },
    { title: "Private direction prepared only when there is a strong fit" },
    { title: "No initial commitment required" },
  ],
};

export const HR_RECEIVE_DEFAULTS = {
  eyebrow: "What you may receive",
  headline_main: "What the homepage preview",
  headline_italic: "can include.",
  intro:
    "When there is a strong fit, the preview is designed to give you a focused direction — not a generic redesign, and not a full free project.",
  cards: [
    { title: "Homepage direction",
      body: "A focused view of how your homepage could be structured for stronger clarity, storytelling, and conversion." },
    { title: "UX and visual hierarchy observations",
      body: "Key opportunities around layout, messaging, content flow, trust signals, and user journey." },
    { title: "Brand and ecommerce positioning",
      body: "Recommendations on how the experience can feel more premium, credible, and aligned with your growth goals." },
    { title: "Next-step clarity",
      body: "If there is a good fit, we’ll outline what a deeper redesign, CRO, or Shopify development phase could look like." },
  ],
};

export const HR_NOT_DEFAULTS = {
  eyebrow: "Clear expectations from the start",
  headline_main: "What this is",
  headline_italic: "not.",
  body:
    "This review is intentionally focused. It is not meant to replace a full UX audit, CRO strategy, or complete Shopify redesign.",
  bullets: [
    { title: "Not a full website redesign" },
    { title: "Not production-ready Shopify development" },
    { title: "Not unlimited revisions or free consulting" },
    { title: "Not a replacement for a detailed CRO or technical audit" },
  ],
  closing:
    "The goal is simple: to give qualified Shopify brands a transparent first look at how Sunday Digital thinks, structures, and improves ecommerce experiences.",
};

export const HR_HOW_DEFAULTS = {
  eyebrow: "How the review works",
  headline_main: "Three steps,",
  headline_italic: "no fluff.",
  steps: [
    { title: "Submit your Shopify store",
      body: "Share your website, contact details, and a few notes about what you want to improve." },
    { title: "We review the opportunity",
      body: "Sunday Digital manually reviews each request to understand whether a homepage preview would be useful and relevant." },
    { title: "We follow up with the next best step",
      body: "If there is a strong fit, we may prepare a private homepage preview or recommend the most relevant next step." },
  ],
};

export const HR_WHO_DEFAULTS = {
  eyebrow: "Built for existing Shopify brands",
  headline_main: "Who this is",
  headline_italic: "for.",
  body:
    "This review is best suited for brands already selling on Shopify or Shopify Plus that want a clearer, more premium, and more conversion-focused storefront experience.",
  good_label: "Good fit",
  good_subheading: "Ideal for these brands",
  good_items: [
    { title: "Existing Shopify or Shopify Plus stores" },
    { title: "Brands considering a redesign or homepage improvement" },
    { title: "Ecommerce teams looking for better UX and conversion flow" },
    { title: "Businesses that want a stronger first impression before investing in a larger project" },
  ],
  not_label: "Not ideal for",
  not_subheading: "Better paths elsewhere",
  not_items: [
    { title: "New businesses without an existing store" },
    { title: "Brands looking only for free design work" },
    { title: "Non-Shopify websites" },
    { title: "Projects with no near-term intention to improve the store" },
  ],
};

export const HR_FORM_DEFAULTS = {
  eyebrow: "Request your private homepage review",
  headline_main: "Tell us about",
  headline_italic: "your store.",
  intro:
    "Share your Shopify store details below. We'll review your request manually and follow up with the most relevant next step.",
  label_name: "Name",
  ph_name: "Your name",
  label_email: "Email",
  ph_email: "you@company.com",
  label_website: "Website URL",
  ph_website: "https://yourstore.com",
  label_platform: "Current platform",
  label_goals: "What are you looking to improve?",
  help_goals: "Select all that apply.",
  label_revenue: "Estimated monthly revenue",
  revenue_optional_label: "(optional)",
  label_message: "Short message / notes",
  message_optional_label: "(optional)",
  ph_message:
    "Anything you'd like us to know about your goals or what's holding the store back…",
  submit_label: "Submit Review Request",
  submit_loading: "Sending…",
  small_text:
    "Every request is reviewed manually. A homepage preview is prepared only when we see a clear opportunity and strong fit.",
  req_message: false,
  req_platform: false,
  req_goals: false,
  req_revenue: false,
  platform_options: [
    { title: "Shopify" },
    { title: "Shopify Plus" },
    { title: "Other" },
  ],
  goal_options: [
    { title: "Homepage clarity" },
    { title: "Brand perception" },
    { title: "Conversion rate" },
    { title: "Full redesign" },
    { title: "Custom Shopify features" },
    { title: "CRO" },
    { title: "Not sure yet" },
  ],
  revenue_options: [
    { title: "Under $50k" },
    { title: "$50k – $250k" },
    { title: "$250k – $1M" },
    { title: "$1M – $5M" },
    { title: "$5M+" },
  ],
  success_eyebrow: "Request received",
  success_headline: "Thank you — your request has been received.",
  success_body:
    "We'll review your Shopify store and follow up with the next best step. If there is a strong fit, we may prepare a private homepage preview.",
};

export const HR_FINAL_DEFAULTS = {
  eyebrow: "Take the first look",
  headline_main: "Want to see what your Shopify homepage",
  headline_italic: "could become?",
  body:
    "Request a private homepage review and get a clearer view of how Sunday Digital could improve your store's first impression, user journey, and conversion potential.",
  cta_label: "Request a Homepage Review",
  trust_items: [
    { title: "Existing Shopify stores only" },
    { title: "Reviewed manually" },
    { title: "No initial commitment" },
  ],
};

export function withSectionDefaults(pageKey, data) {
  const out = { ...(data || {}) };

  // ---- shared mergers ----
  const mergeFlat = (existing, defaults) => {
    const merged = { ...defaults };
    for (const [k, v] of Object.entries(existing || {})) merged[k] = v;
    return merged;
  };
  const mergeWithList = (existing, defaults, listKey) => {
    const exists = Array.isArray(existing?.[listKey]);
    const merged = { ...defaults };
    for (const [k, v] of Object.entries(existing || {})) {
      if (k === listKey && !exists) continue;
      merged[k] = v;
    }
    if (!exists && Array.isArray(defaults[listKey])) {
      merged[listKey] = defaults[listKey].map((it) => ({
        ...it,
        ...(Array.isArray(it.highlights)
          ? { highlights: [...it.highlights] }
          : {}),
      }));
    }
    return merged;
  };

  if (pageKey === "creative") {
    // Hero has two arrays (verbs + marquee_words) — merge each with
    // its own first-open semantics via mergeWithList applied twice.
    let hero = mergeWithList(out.hero, CRE_HERO_DEFAULTS, "verbs");
    hero = mergeWithList(hero, { ...CRE_HERO_DEFAULTS, verbs: hero.verbs }, "marquee_words");
    out.hero                = hero;
    out.tailored_solutions  = mergeWithList(out.tailored_solutions, CRE_TAILORED_SOLUTIONS_DEFAULTS, "items");
    out.stats_band          = mergeWithList(out.stats_band, CRE_STATS_BAND_DEFAULTS, "items");
    out.possibilities_lab   = mergeWithList(out.possibilities_lab, CRE_POSSIBILITIES_LAB_DEFAULTS, "items");
    out.more_services       = mergeWithList(out.more_services, CRE_MORE_SERVICES_DEFAULTS, "items");
    out.latest_projects     = mergeWithList(out.latest_projects, CRE_LATEST_PROJECTS_DEFAULTS, "items");
    out.cross_page_browser  = mergeWithList(out.cross_page_browser, CRE_CROSS_BROWSER_DEFAULTS, "items");
    return out;
  }

  if (pageKey === "about") {
    out.hero           = mergeWithList(out.hero, ABT_HERO_DEFAULTS, "marquee_words");
    out.origin         = mergeFlat(out.origin, ABT_ORIGIN_DEFAULTS);
    out.stats_ribbon   = mergeWithList(out.stats_ribbon, ABT_STATS_RIBBON_DEFAULTS, "items");
    out.they_trust_us  = mergeWithList(out.they_trust_us, ABT_TRUST_DEFAULTS, "items");
    out.how_we_do_it   = mergeWithList(out.how_we_do_it, ABT_HOW_WE_DEFAULTS, "items");
    out.manifesto      = mergeFlat(out.manifesto, ABT_MANIFESTO_DEFAULTS);
    return out;
  }

  if (pageKey === "contact") {
    out.hero           = mergeFlat(out.hero, CONTACT_HERO_DEFAULTS);
    out.routing        = mergeWithList(out.routing, CONTACT_ROUTING_DEFAULTS, "items");
    out.book_a_call    = mergeWithList(out.book_a_call, CONTACT_BOOK_DEFAULTS, "bullets");
    out.faq            = mergeWithList(out.faq, CONTACT_FAQ_DEFAULTS, "items");
    // Direct details has two independent lists (socials + offices).
    let dd = mergeWithList(out.direct_details, CONTACT_DETAILS_DEFAULTS, "socials");
    dd = mergeWithList(dd, { ...CONTACT_DETAILS_DEFAULTS, socials: dd.socials }, "offices");
    out.direct_details = dd;
    out.closing        = mergeFlat(out.closing, CONTACT_CLOSING_DEFAULTS);
    out.form           = mergeFlat(out.form, CONTACT_FORM_DEFAULTS);
    return out;
  }

  if (pageKey === "globals") {
    out.nav    = mergeWithList(out.nav, NAV_DEFAULTS, "overlay_pages");
    // Footer has two lists (columns + legal_links).
    let f = mergeWithList(out.footer, FOOTER_DEFAULTS, "columns");
    f = mergeWithList(f, { ...FOOTER_DEFAULTS, columns: f.columns }, "legal_links");
    out.footer = f;
    return out;
  }

  if (pageKey === "homepage_review") {
    out.hero    = mergeWithList(out.hero,    HR_HERO_DEFAULTS,    "trust_items");
    out.why     = mergeWithList(out.why,     HR_WHY_DEFAULTS,     "points");
    out.receive = mergeWithList(out.receive, HR_RECEIVE_DEFAULTS, "cards");
    out.not     = mergeWithList(out.not,     HR_NOT_DEFAULTS,     "bullets");
    out.how     = mergeWithList(out.how,     HR_HOW_DEFAULTS,     "steps");
    // `who` has 2 independent bullet lists.
    let who     = mergeWithList(out.who,     HR_WHO_DEFAULTS,     "good_items");
    who         = mergeWithList(who,         { ...HR_WHO_DEFAULTS, good_items: who.good_items }, "not_items");
    out.who     = who;
    // Form has 3 option lists — merge each in turn so the prior
    // mergeWithList outputs survive (it would otherwise overwrite the
    // already-seeded keys).
    let form    = mergeWithList(out.form,    HR_FORM_DEFAULTS,    "platform_options");
    form        = mergeWithList(form,        { ...HR_FORM_DEFAULTS, platform_options: form.platform_options }, "goal_options");
    form        = mergeWithList(form,        { ...HR_FORM_DEFAULTS, platform_options: form.platform_options, goal_options: form.goal_options }, "revenue_options");
    out.form    = form;
    out.final   = mergeWithList(out.final,   HR_FINAL_DEFAULTS,   "trust_items");
    return out;
  }

  if (pageKey === "showcase") {
    out.hero    = mergeFlat(out.hero, SHOWCASE_HERO_DEFAULTS);
    out.marquee = mergeWithList(out.marquee, SHOWCASE_MARQUEE_DEFAULTS, "items");
    out.closing = mergeFlat(out.closing, SHOWCASE_CLOSING_DEFAULTS);
    return out;
  }

  if (pageKey === "privacy") {
    out.header = mergeFlat(out.header, PRIVACY_DEFAULTS.header);
    out.body   = mergeFlat(out.body,   PRIVACY_DEFAULTS.body);
    return out;
  }

  if (pageKey === "terms") {
    out.header = mergeFlat(out.header, TERMS_DEFAULTS.header);
    out.body   = mergeFlat(out.body,   TERMS_DEFAULTS.body);
    return out;
  }

  if (pageKey === "marketing") {
    out.hero          = mergeFlat(out.hero, MKT_HERO_DEFAULTS);
    out.trust_strip   = mergeWithList(out.trust_strip, MKT_TRUST_STRIP_DEFAULTS, "items");
    out.why_us        = mergeWithList(out.why_us, MKT_WHY_US_DEFAULTS, "columns");
    out.method        = mergeWithList(out.method, MKT_METHOD_DEFAULTS, "items");
    out.services_grid = mergeWithList(out.services_grid, MKT_SERVICES_GRID_DEFAULTS, "items");
    out.showreel      = mergeFlat(out.showreel, MKT_SHOWREEL_DEFAULTS);
    return out;
  }

  if (pageKey === "shopify-dev") {
    out.hero               = mergeFlat(out.hero, SDEV_HERO_DEFAULTS);
    out.trust_strip        = mergeWithList(out.trust_strip, SDEV_TRUST_STRIP_DEFAULTS, "items");
    out.what_you_get       = mergeWithList(out.what_you_get, SDEV_WHAT_YOU_GET_DEFAULTS, "items");
    out.capabilities       = mergeWithList(out.capabilities, SDEV_CAPABILITIES_DEFAULTS, "items");
    out.build_showcase     = mergeWithList(out.build_showcase, SDEV_BUILD_SHOWCASE_DEFAULTS, "stats");
    out.possibilities      = mergeWithList(out.possibilities, SDEV_POSSIBILITIES_DEFAULTS, "buckets");
    out.process            = mergeWithList(out.process, SDEV_PROCESS_DEFAULTS, "items");
    out.tech_stack         = mergeWithList(out.tech_stack, SDEV_TECH_STACK_DEFAULTS, "items");
    out.who_we_work_with   = mergeFlat(out.who_we_work_with, SDEV_WHO_WE_WORK_WITH_DEFAULTS);
    return out;
  }

  if (pageKey === "home") {
    // Hero: fill any missing keys from the live snapshot so the form
    // renders pre-populated on first open. Admin-set values (including
    // empty strings) always win.
    const heroExisting = out.hero || {};
    const heroMerged = { ...HOME_HERO_DEFAULTS };
    for (const [k, v] of Object.entries(heroExisting)) {
      heroMerged[k] = v;
    }
    out.hero = heroMerged;

    // Development overview: single body field.
    const devExisting = out.development_overview || {};
    const devMerged = { ...HOME_DEVELOPMENT_OVERVIEW_DEFAULTS };
    for (const [k, v] of Object.entries(devExisting)) {
      devMerged[k] = v;
    }
    out.development_overview = devMerged;

    // Hero video: single media field (video URL).
    const hvExisting = out.hero_video || {};
    const hvMerged = { ...HOME_HERO_VIDEO_DEFAULTS };
    for (const [k, v] of Object.entries(hvExisting)) {
      hvMerged[k] = v;
    }
    out.hero_video = hvMerged;

    // Our Shopify services: header fields + reorderable list of items.
    // We only seed `items` when no items have ever been saved (i.e.
    // first open). Once the admin has touched the list — even to
    // reorder once — their version wins.
    const sgExisting = out.services_grid || {};
    const sgItemsExist = Array.isArray(sgExisting.items);
    const sgMerged = { ...HOME_SERVICES_GRID_DEFAULTS };
    for (const [k, v] of Object.entries(sgExisting)) {
      if (k === "items" && !sgItemsExist) continue;
      sgMerged[k] = v;
    }
    if (!sgItemsExist) {
      sgMerged.items = HOME_SERVICES_GRID_DEFAULTS.items.map((it) => ({
        ...it,
        highlights: [...(it.highlights || [])],
      }));
    }
    out.services_grid = sgMerged;

    // Media background: header fields + single full-bleed media.
    const mbExisting = out.media_background || {};
    const mbMerged = { ...HOME_MEDIA_BACKGROUND_DEFAULTS };
    for (const [k, v] of Object.entries(mbExisting)) {
      mbMerged[k] = v;
    }
    out.media_background = mbMerged;

    // Marketing 360 pinned: header fields + 2 CTAs + reorderable
    // panel list. Same first-open logic as services_grid.
    const mpExisting = out.marketing_pinned || {};
    const mpPanelsExist = Array.isArray(mpExisting.panels);
    const mpMerged = { ...HOME_MARKETING_PINNED_DEFAULTS };
    for (const [k, v] of Object.entries(mpExisting)) {
      if (k === "panels" && !mpPanelsExist) continue;
      mpMerged[k] = v;
    }
    if (!mpPanelsExist) {
      mpMerged.panels = HOME_MARKETING_PINNED_DEFAULTS.panels.map((p) => ({ ...p }));
    }
    out.marketing_pinned = mpMerged;

    // Creative capabilities: header fields + reorderable card list.
    const ccExisting = out.creative_capabilities || {};
    const ccItemsExist = Array.isArray(ccExisting.items);
    const ccMerged = { ...HOME_CREATIVE_CAPABILITIES_DEFAULTS };
    for (const [k, v] of Object.entries(ccExisting)) {
      if (k === "items" && !ccItemsExist) continue;
      ccMerged[k] = v;
    }
    if (!ccItemsExist) {
      ccMerged.items = HOME_CREATIVE_CAPABILITIES_DEFAULTS.items.map((it) => ({ ...it }));
    }
    out.creative_capabilities = ccMerged;

    // Case studies: header fields + reorderable project list.
    const csExisting = out.case_studies || {};
    const csItemsExist = Array.isArray(csExisting.items);
    const csMerged = { ...HOME_CASE_STUDIES_DEFAULTS };
    for (const [k, v] of Object.entries(csExisting)) {
      if (k === "items" && !csItemsExist) continue;
      csMerged[k] = v;
    }
    if (!csItemsExist) {
      csMerged.items = HOME_CASE_STUDIES_DEFAULTS.items.map((it) => ({
        ...it,
        highlights: [...(it.highlights || [])],
      }));
    }
    out.case_studies = csMerged;

    // Testimonials: eyebrow + reorderable list of quotes.
    const tsExisting = out.testimonials || {};
    const tsItemsExist = Array.isArray(tsExisting.items);
    const tsMerged = { ...HOME_TESTIMONIALS_DEFAULTS };
    for (const [k, v] of Object.entries(tsExisting)) {
      if (k === "items" && !tsItemsExist) continue;
      tsMerged[k] = v;
    }
    if (!tsItemsExist) {
      tsMerged.items = HOME_TESTIMONIALS_DEFAULTS.items.map((it) => ({ ...it }));
    }
    out.testimonials = tsMerged;

    // FAQ: header copy + reorderable Q/A list.
    const faqExisting = out.faq || {};
    const faqItemsExist = Array.isArray(faqExisting.items);
    const faqMerged = { ...HOME_FAQ_DEFAULTS };
    for (const [k, v] of Object.entries(faqExisting)) {
      if (k === "items" && !faqItemsExist) continue;
      faqMerged[k] = v;
    }
    if (!faqItemsExist) {
      faqMerged.items = HOME_FAQ_DEFAULTS.items.map((it) => ({ ...it }));
    }
    out.faq = faqMerged;

    // Closing CTA: flat fields + reorderable tagline word list.
    const ccCtaExisting = out.closing_cta || {};
    const ccCtaWordsExist = Array.isArray(ccCtaExisting.tagline_words);
    const ccCtaMerged = { ...HOME_CLOSING_CTA_DEFAULTS };
    for (const [k, v] of Object.entries(ccCtaExisting)) {
      if (k === "tagline_words" && !ccCtaWordsExist) continue;
      ccCtaMerged[k] = v;
    }
    if (!ccCtaWordsExist) {
      ccCtaMerged.tagline_words = HOME_CLOSING_CTA_DEFAULTS.tagline_words.map((w) => ({ ...w }));
    }
    out.closing_cta = ccCtaMerged;

    const existing = out.logo_strip || {};
    const line1 = Array.isArray(existing.line_1) ? existing.line_1 : null;
    const line2 = Array.isArray(existing.line_2) ? existing.line_2 : null;
    // Only pre-fill if BOTH lines are missing (i.e. this is a first-time
    // open). If the admin has partial data, respect it.
    if (!line1 && !line2) {
      out.logo_strip = {
        ...HOME_LOGO_STRIP_DEFAULTS,
        ...existing,
        line_1: HOME_LOGO_STRIP_DEFAULTS.line_1.map((l) => ({ ...l })),
        line_2: HOME_LOGO_STRIP_DEFAULTS.line_2.map((l) => ({ ...l })),
        line_1_speed:
          existing.line_1_speed != null
            ? existing.line_1_speed
            : HOME_LOGO_STRIP_DEFAULTS.line_1_speed,
        line_2_speed:
          existing.line_2_speed != null
            ? existing.line_2_speed
            : HOME_LOGO_STRIP_DEFAULTS.line_2_speed,
      };
    }
    // Editorial collage: fill any missing keys from the live snapshot so
    // the form renders pre-populated on first open. Admin-set values
    // (including empty strings) always win.
    const ecExisting = out.editorial_collage || {};
    const ecMerged = { ...HOME_EDITORIAL_COLLAGE_DEFAULTS };
    for (const [k, v] of Object.entries(ecExisting)) {
      ecMerged[k] = v;
    }
    out.editorial_collage = ecMerged;
  }
  return out;
}

/** List of (pageKey, sectionId) pairs that have live-site defaults to
 * show a banner for. Used by ContentEditPage to display a note. */
export function sectionHasSeededDefaults(pageKey, sectionId, data) {
  if (pageKey !== "home") return false;
  if (sectionId !== "logo_strip") return false;
  const ls = data?.logo_strip || {};
  const line1 = Array.isArray(ls.line_1) ? ls.line_1 : [];
  const line2 = Array.isArray(ls.line_2) ? ls.line_2 : [];
  if (line1.length !== HOME_LOGO_STRIP_DEFAULTS.line_1.length) return false;
  if (line2.length !== HOME_LOGO_STRIP_DEFAULTS.line_2.length) return false;
  // Rough identity check — if each slot has the same media URL as the
  // default, we consider this a pristine pre-fill.
  const all = [...line1, ...line2];
  const defaults = [
    ...HOME_LOGO_STRIP_DEFAULTS.line_1,
    ...HOME_LOGO_STRIP_DEFAULTS.line_2,
  ];
  return all.every((l, i) => (l.media || "") === defaults[i].media);
}
